import React, { useState, useEffect } from "react";
import axios from "axios";
import {
  Dialog,
  DialogContent,
  DialogActions,
  Typography,
  Grid,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Radio,
  RadioGroup,
  FormControlLabel,
  CircularProgress,
  makeStyles,
} from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import ChevronRightIcon from "@material-ui/icons/ChevronRight";
import { appUrl } from "../../../../../../utils/config";

const useStyles = makeStyles(() => ({
  cancelButton: {
    fontSize: "1rem",
    fontWeight: 500,
    display: "flex",
    alignItems: "center",
    cursor: "pointer",
    color: "#FF0000",
  },
  dialogTitle: {
    textAlign: "center",
    fontWeight: 700,
    fontSize: "1.5rem",
    marginBottom: "6px",
  },
  dialogSubtitle: {
    textAlign: "center",
    fontSize: "0.9rem",
    color: "#444",
    marginBottom: "20px",
  },
  infoHeaderRow: {
    backgroundColor: "#EBF3FB",
    border: "1px solid #c5ddf0",
    borderRadius: "6px",
    padding: "10px 20px",
    marginBottom: "20px",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    flexWrap: "wrap",
    gap: "8px",
  },
  infoLabel: {
    fontWeight: 700,
    fontSize: "0.78rem",
    color: "#555",
    marginBottom: "2px",
  },
  infoValue: {
    fontSize: "0.88rem",
    fontWeight: 500,
  },
  tableContainer: {
    boxShadow: "none",
    border: "1px solid #d0d0d0",
    borderRadius: "6px",
    marginBottom: "14px",
    overflowX: "hidden",
  },
  tableHeaderCell: {
    fontWeight: 700,
    backgroundColor: "#F5F5F5",
    fontSize: "0.72rem",
    padding: "8px 8px",
    whiteSpace: "normal",
    lineHeight: 1.3,
  },
  tableCell: {
    fontSize: "0.78rem",
    padding: "8px 8px",
  },
  noteText: {
    fontSize: "0.78rem",
    color: "#333",
    marginBottom: "12px",
    lineHeight: 1.5,
  },
  radioGroup: {
    flexDirection: "row",
    gap: "24px",
    marginBottom: "4px",
  },
  radioLabel: {
    fontSize: "0.82rem",
  },
  orangeRadio: {
    color: "#000000",
    "&.Mui-checked": {
      color: "#EC6535",
    },
  },
  confirmButton: {
    backgroundColor: "#EC6535",
    color: "#fff",
    borderRadius: "20px",
    textTransform: "none",
    padding: "8px 32px",
    fontWeight: 500,
    "&:hover": {
      backgroundColor: "#d4572e",
    },
  },
  errorText: {
    color: "#D32F2F",
    fontSize: "0.82rem",
    marginTop: "8px",
    textAlign: "center",
  },
  successDialog: {
    textAlign: "center",
    padding: "40px 20px",
  },
  successIcon: {
    fontSize: "80px",
    color: "#39811D",
    marginBottom: "20px",
  },
  successTitle: {
    fontSize: "24px",
    fontWeight: "bold",
    color: "#333",
    marginBottom: "16px",
  },
  okButton: {
    backgroundColor: "#EC6535",
    color: "#fff",
    borderRadius: "20px",
    textTransform: "none",
    padding: "8px 24px",
    marginTop: "20px",
    "&:hover": {
      backgroundColor: "#d4572e",
    },
  },
}));

/**
 * Build the table rows for the Review Consumption dialog.
 * allocationData: fresh array from fetchComponentAllocationPlannedAndActualNew (fetched by this dialog)
 * originalPOQty: PO qty before variance approval
 * newPOQty: PO qty after variance approval (= produced qty)
 * plannedQtySnapshot: map of "componentCode__lotNumber" → plannedAllocatedQuantity,
 *   captured when VarianceMismatchDialog first opened (FE memory for Previous Planned Allocated Qty)
 */
const buildReviewRows = (allocationData, originalPOQty, newPOQty, plannedQtySnapshot) => {
  if (!Array.isArray(allocationData)) return [];

  return allocationData.map((item) => {
    const planned = item.plannedLotNumberAllocationDetails || [];
    const actual = item.actualConsumptionDetails || [];

    // Build lot map from planned lots
    const lotMap = {};
    planned.forEach((p) => {
      const key = p.lotNumber || "unknown";
      const lookupKey = `${item.componentCode}__${p.lotNumber}`;
      const previousPlannedQty =
        plannedQtySnapshot && plannedQtySnapshot[lookupKey] !== undefined
          ? plannedQtySnapshot[lookupKey]
          : p.plannedAllocatedQuantity || 0;
      lotMap[key] = {
        batchNumber: p.lotNumber || "-",
        previousPlannedQty,
        // updatedPlannedQty taken from plannedLotNumberAllocationDetails → plannedAllocatedQuantity
        updatedPlannedQty: parseFloat((p.plannedAllocatedQuantity || 0).toFixed(3)),
        componentBatchNumber: p.lotNumber || "",
      };
    });

    // Merge actual batch numbers not already in planned
    actual.forEach((a) => {
      const key = a.componentBatchNumber || "unknown";
      if (!lotMap[key]) {
        const lookupKey = `${item.componentCode}__${a.componentBatchNumber}`;
        const previousPlannedQty =
          plannedQtySnapshot && plannedQtySnapshot[lookupKey] !== undefined
            ? plannedQtySnapshot[lookupKey]
            : 0;
        lotMap[key] = {
          batchNumber: a.componentBatchNumber || "-",
          previousPlannedQty,
          updatedPlannedQty: 0,
          componentBatchNumber: a.componentBatchNumber || "",
        };
      }
    });

    const lots = Object.values(lotMap);

    return {
      materialCode: item.componentCode || "-",
      materialName: item.componentName || "-",
      // updatedBomQty taken directly from API response
      updatedBomQty: item.componentQuantity != null ? parseFloat(item.componentQuantity.toFixed(3)) : "-",
      uom: item.bomComponentRefUOM || item.uom || "-",
      lots,
      // Raw data needed for API call
      bomComponentCode: item.componentCode || "",
      BomComponentCodeCMO: item.cmoComponentCode || "",
      bomComponentName: item.componentName || "",
      bomComponentRefUOM: item.bomComponentRefUOM || item.uom || "",
      BomComponentRefUOMCMO: "",
    };
  });
};

const ReviewConsumptionDialog = ({
  open,
  handleClose,
  row,
  plannedQtySnapshot,
  originalPOQty,
  newPOQty,
  onRefreshData,
}) => {
  const classes = useStyles();
  const [consumedQtyOption, setConsumedQtyOption] = useState("previous");
  const [confirming, setConfirming] = useState(false);
  const [confirmError, setConfirmError] = useState("");
  const [confirmSuccess, setConfirmSuccess] = useState(false);

  // Fresh allocation data fetched by this dialog when it opens
  const [freshAllocationData, setFreshAllocationData] = useState([]);
  const [loadingAllocation, setLoadingAllocation] = useState(false);

  useEffect(() => {
    if (!open) return;

    const batchID = row?.schrockenDeliveryScheduleBatchID;
    const batchSize = row?.batchSize;
    if (!batchID || !batchSize) return;

    setLoadingAllocation(true);
    const url = `${appUrl}/${localStorage.appName}/${localStorage.network}/${localStorage.collabType}/${localStorage.roleName}/fetchComponentAllocationPlannedAndActualNew/${batchID}/${batchSize}`;

    axios
      .get(url)
      .then((res) => {
        if (res.data.status) {
          setFreshAllocationData(res.data.data || []);
        } else {
          setFreshAllocationData([]);
        }
      })
      .catch((err) => {
        console.error("Error fetching allocation data for review:", err);
        setFreshAllocationData([]);
      })
      .finally(() => setLoadingAllocation(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const reviewRows = buildReviewRows(freshAllocationData, originalPOQty, newPOQty, plannedQtySnapshot);

  const noteText = `Note - The Previous Planned Allocated Qty is derived from the Allocation which was done when PO Qty was ${
    originalPOQty || "-"
  } ${row?.batchUOM || ""}. The Updated Planned Allocated Qty is auto-calculated value based on the PO Qty change to ${
    newPOQty || "-"
  } ${row?.batchUOM || ""}. Select appropriate Consumed Qty from below.`;

  const handleConfirm = async () => {
    if (!consumedQtyOption) return;

    setConfirming(true);
    setConfirmError("");

    // Build BOMConsumptionDetails from reviewRows using selected option
    const BOMConsumptionDetails = reviewRows.map((material) => ({
      BomComponentCode: material.bomComponentCode,
      bomComponentCodeCMO: material.BomComponentCodeCMO,
      BomComponentName: material.bomComponentName,
      BomComponentRefUOM: material.bomComponentRefUOM,
      bomComponentRefUOMCMO: material.BomComponentRefUOMCMO,
      consumptionBatchNumbers: material.lots.map((lot) => ({
        componentBatchNumber: lot.componentBatchNumber,
        componentBatchNumberCMO: "",
        BomComponentConsumedQuantity:
          consumedQtyOption === "previous"
            ? lot.previousPlannedQty
            : lot.updatedPlannedQty,
        bomComponentConsumedQuantityCMO: 0,
      })),
    }));

    const payload = {
      vendorBatchNumber:row?.vendorBatchNumber,
      vendorMosymphonyID: row?.vendorMosymphonyID || null,
      BOMConsumptionDataList: [
        {
          scheduleAgreementNumber: row?.scheduleAgreementNumber || "",
          schrockenSLID: row?.schrockenSLID || null,
          schrockenDeliveryScheduleBatchID:
            row?.schrockenDeliveryScheduleBatchID || null,
          BOMConsumptionDetails,
        },
      ],
    };

    try {
      const url = `${appUrl}/${localStorage.appName}/${localStorage.network}/${localStorage.collabType}/${localStorage.roleName}/updateBOMConsumedQty`;
      const res = await axios.post(url, payload);

      if (res.data.status || res.data.success) {
        setConfirmSuccess(true);
      } else {
        setConfirmError(res.data.message || "Update failed. Please try again.");
      }
    } catch (err) {
      console.error("Error updating BOM consumed qty:", err);
      setConfirmError(
        err.response?.data?.message || "An error occurred. Please try again."
      );
    } finally {
      setConfirming(false);
    }
  };

  return (
    <>
    <Dialog
      open={open && !confirmSuccess}
      onClose={(event, reason) => {
        if (reason === "backdropClick" || reason === "escapeKeyDown") return;
        handleClose();
      }}
      fullWidth
      maxWidth="lg"
      PaperProps={{ style: { overflowX: "hidden" } }}
    >
      <DialogContent style={{ padding: "16px 24px 8px 24px", overflowX: "hidden" }}>
        <>
        {/* Cancel */}
        {/* <Grid container justifyContent="flex-end" style={{ marginBottom: "8px" }}>
          <Typography
            variant="body2"
            className={classes.cancelButton}
            onClick={handleClose}
          >
            Cancel&nbsp;
            <CloseIcon fontSize="large" />
          </Typography>
        </Grid> */}

        {/* Title */}
        <Typography className={classes.dialogTitle}>Review Consumption</Typography>
        <Typography className={classes.dialogSubtitle}>
          Confirm that consumption details match allocation
        </Typography>

        {/* Header info */}
        <div className={classes.infoHeaderRow}>
          {[
            { label: "CMO", value: row?.vendorName || "-" },
            {
              label: "Product Code / Description",
              value:
                row?.fgMaterialCode && row?.fgMaterialName
                  ? `${row.fgMaterialCode} / ${row.fgMaterialName}`
                  : "-",
            },
            { label: "Batch Number", value: row?.vendorBatchNumber || "-" },
            { label: "Batch Size", value: row?.batchSize || "-" },
            { label: "UOM", value: row?.batchUOM || "-" },
            { label: "PO Number", value: row?.scheduleAgreementNumber || "-" },
          ].map(({ label, value }) => (
            <div key={label}>
              <Typography className={classes.infoLabel}>{label}</Typography>
              <Typography className={classes.infoValue}>{value}</Typography>
            </div>
          ))}
        </div>

        {/* Materials table */}
        <TableContainer
          component={Paper}
          className={classes.tableContainer}
          style={{ overflowX: "hidden" }}
        >
          <Table size="small">
            <TableHead>
              <TableRow>
                {[
                  "Sponsor Material Code / Description",
                  "Updated BoM Quantity",
                  "UOM",
                  "Sponsor Material Batch Numbers",
                  "Previous Planned Allocated Qty",
                  "Updated Planned Allocated Qty",
                ].map((col) => (
                  <TableCell key={col} className={classes.tableHeaderCell}>
                    {col}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {loadingAllocation ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" className={classes.tableCell}>
                    <CircularProgress size={24} />
                  </TableCell>
                </TableRow>
              ) : reviewRows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" className={classes.tableCell}>
                    No allocation data available
                  </TableCell>
                </TableRow>
              ) : (
                reviewRows.map((material, mIdx) =>
                  material.lots.map((lot, lIdx) => (
                    <TableRow key={`${mIdx}-${lIdx}`}>
                      {lIdx === 0 && (
                        <TableCell
                          className={classes.tableCell}
                          rowSpan={material.lots.length}
                          style={{ verticalAlign: "middle" }}
                        >
                          <div>{material.materialCode} /</div>
                          <div>{material.materialName}</div>
                        </TableCell>
                      )}
                      {lIdx === 0 && (
                        <TableCell
                          className={classes.tableCell}
                          rowSpan={material.lots.length}
                          style={{ verticalAlign: "middle" }}
                        >
                          {material.updatedBomQty}
                        </TableCell>
                      )}
                      {lIdx === 0 && (
                        <TableCell
                          className={classes.tableCell}
                          rowSpan={material.lots.length}
                          style={{ verticalAlign: "middle" }}
                        >
                          {material.uom}
                        </TableCell>
                      )}
                      <TableCell className={classes.tableCell}>
                        {lot.batchNumber}
                      </TableCell>
                      <TableCell className={classes.tableCell}>
                        {lot.previousPlannedQty}
                      </TableCell>
                      <TableCell className={classes.tableCell}>
                        {lot.updatedPlannedQty}
                      </TableCell>
                    </TableRow>
                  ))
                )
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Note */}
        <Typography className={classes.noteText}>{noteText}</Typography>

        {/* Radio options */}
        <RadioGroup
          value={consumedQtyOption}
          onChange={(e) => setConsumedQtyOption(e.target.value)}
          className={classes.radioGroup}
        >
          <FormControlLabel
            value="previous"
            control={<Radio className={classes.orangeRadio} size="small" />}
            label={
              <Typography className={classes.radioLabel}>
                Consumed Qty = Previous Planned Allocated Qty
              </Typography>
            }
          />
          <FormControlLabel
            value="updated"
            control={<Radio className={classes.orangeRadio} size="small" />}
            label={
              <Typography className={classes.radioLabel}>
                Consumed Qty = Updated Planned Allocated Qty
              </Typography>
            }
          />
        </RadioGroup>

        {confirmError && (
          <Typography className={classes.errorText}>{confirmError}</Typography>
        )}
        </>
      </DialogContent>

      <DialogActions style={{ justifyContent: "center", padding: "16px 36px 24px 36px" }}>
        <Button
          variant="contained"
          className={classes.confirmButton}
          onClick={handleConfirm}
          disabled={!consumedQtyOption || confirming}
          startIcon={confirming ? <CircularProgress size={16} color="inherit" /> : null}
        >
          {confirming ? "Confirming..." : "Confirm Consumption"}
        </Button>
      </DialogActions>
    </Dialog>

    {/* Success Dialog */}
    <Dialog
      open={confirmSuccess}
      onClose={(event, reason) => {
        if (reason === "backdropClick" || reason === "escapeKeyDown") return;
        setConfirmSuccess(false);
        handleClose();
      }}
      maxWidth="sm"
      fullWidth
    >
      <DialogContent className={classes.successDialog}>
        <CheckCircleIcon className={classes.successIcon} />
        <Typography className={classes.successTitle}>
          Consumption Confirmed Successfully
        </Typography>
        <Button
          variant="contained"
          className={classes.okButton}
          onClick={() => {
            setConfirmSuccess(false);
            handleClose();
          }}
          endIcon={<ChevronRightIcon />}
        >
          OK
        </Button>
      </DialogContent>
    </Dialog>
    </>
  );
};

export default ReviewConsumptionDialog;
